import * as i0 from '@angular/core';
import { Component, Input, EventEmitter, Output, ContentChild, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { coerceBooleanProperty } from '@roosevelt/common-ui-lib/core';
import { IconComponent, IconModule } from '@roosevelt/common-ui-lib/icon';

/**
 * Card Floating Header Component
 *
 * @description Header which floats behind the top of the card
 * @example
 * <!-- rs-doc {link: 'Card with h4 floating header'} -->
 * <rs-card>
 *  <rs-card-floating-header> <h4 style="display: flex; align-items: center; margin-top: 0;"><rs-icon>previous</rs-icon> Back To Results</h4> </rs-card-floating-header>
 *   <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 * @example
 * <!-- rs-doc {link: 'Card with secondary header'} -->
 * <rs-card>
 *  <rs-card-floating-header color="secondary"> <h4 style="display: flex; align-items: center; margin-top: 0;"><rs-icon>previous</rs-icon> Back To Results</h4> </rs-card-floating-header>
 *   <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 */
class CardFloatingHeaderComponent {
    /**
     * Allows the host component to change colors, based on a theme
     */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        this._color = 'primary';
    }
    ngOnInit() {
        this.color = this.color;
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardFloatingHeaderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: CardFloatingHeaderComponent, isStandalone: true, selector: "rs-card-floating-header", inputs: { color: "color" }, ngImport: i0, template: "<div class=\"rs-card-floating-header\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card-floating-header{width:100%;min-height:30px;padding:30px 30px 70px;margin-bottom:-100px}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardFloatingHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-card-floating-header', standalone: true, template: "<div class=\"rs-card-floating-header\">\n  <ng-content></ng-content>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card-floating-header{width:100%;min-height:30px;padding:30px 30px 70px;margin-bottom:-100px}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { color: [{
                type: Input
            }] } });
/**
 * Card Header Component
 *
 * @description Header which snaps to the top of the card
 * @example
 * <!-- rs-doc {link: 'Expandable card with plain text header, all colors'} -->
 * <rs-card>
 *  <rs-card-header expandable color="basic" [isExpanded]="false">
 *  <span style="flex: 2" >Plan: DDMI</span>
 *   <span style="flex: 2">Subscriber SSN: 1111</span>
 *   <span style="flex: 2">Subscriber CAID: 2222</span>
 *   <span style="flex: 2">Client Id: 9557</span>
 *   <span style="flex: 2">Sub Client Id: 9050</span>
 * </rs-card-header>
 *  <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 * <rs-card>
 *  <rs-card-header expandable [isExpanded]="false">
 * <span style="flex: 2">Plan: DDMI</span>
 *   <span style="flex: 2">Subscriber SSN: 1111</span>
 *   <span style="flex: 2">Subscriber CAID: 2222</span>
 *   <span style="flex: 2">Client Id: 9557</span>
 *   <span style="flex: 2">Sub Client Id: 9050</span>
 * </rs-card-header>
 *  <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 * <rs-card>
 *  <rs-card-header expandable color="secondary" [isExpanded]="false">
 * <span style="flex: 2">Plan: DDMI</span>
 *   <span style="flex: 2">Subscriber SSN: 1111</span>
 *   <span style="flex: 2">Subscriber CAID: 2222</span>
 *   <span style="flex: 2">Client Id: 9557</span>
 *   <span style="flex: 2">Sub Client Id: 9050</span>
 * </rs-card-header>
 *  <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 * @example
 * <!-- rs-doc {link: 'Card with h2 header'} -->
 * <rs-card>
 *  <rs-card-header> <h2>Test</h2> </rs-card-header>
 *   <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 * @example
 * <!-- rs-doc {link: 'Card with header and action button(close)'} -->
 * <rs-card>
 *  <rs-card-header>
 *   <div style="width: 100%; display: flex; align-items: center;">
 *    <span style="flex: 2">Test</span>
 *    <span style="flex: 2" style="flex: 2;"></span>
 *    <svg xmlns="http://www.w3.org/2000/svg" style="cursor: pointer;" width="24" height="24" viewBox="0 0 24 24">
 *     <path fill="white" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
 *     <path d="M0 0h24v24H0z" fill="none" />
 *    </svg>
 *   </div>
 *  </rs-card-header>
 *  <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 */
class CardHeaderComponent {
    /**
     * Allows the host component to expand
     */
    get expandable() {
        return this._expandable;
    }
    set expandable(value) {
        this._expandable = coerceBooleanProperty(value);
    }
    /**
     * Allows user to force the state of the expandable header
     */
    get isExpanded() {
        return this._isExpanded;
    }
    set isExpanded(value) {
        this._isExpanded = coerceBooleanProperty(value);
        this.expanded.emit(this.isExpanded);
    }
    /**
     * Allows the host component to change colors, based on a theme
     */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    constructor(elementRef) {
        this.elementRef = elementRef;
        /**
         * When ever the header is expanded emit if it is expanded
         */
        this.expanded = new EventEmitter();
        this._color = 'primary';
        this._isExpanded = true;
        this._expandable = false;
    }
    ngOnInit() {
        this.color = this.color;
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    expand() {
        if (this.expandable) {
            this.isExpanded = !this.isExpanded;
        }
    }
    expandWithEnter(e) {
        if (e.key === 'Enter') {
            this.expand();
        }
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_expandable = undefined; }
    static { this.ngAcceptInputType_isExpanded = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardHeaderComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: CardHeaderComponent, isStandalone: true, selector: "rs-card-header", inputs: { expandable: "expandable", isExpanded: "isExpanded", color: "color" }, outputs: { expanded: "expanded" }, ngImport: i0, template: "<div tabindex=\"0\" role=\"dialog\" class=\"rs-card-header\" aria-label=\"dialog-content\"\n  [class.expanded]=\"expandable && isExpanded\"\n  [class.collapsed]=\"expandable && !isExpanded\"\n  (click)=\"expand()\"\n  (keydown)=\"expandWithEnter($event)\">\n  <ng-content></ng-content>\n  <span style=\"flex: 1;\"></span>\n  @if (expandable) {\n    <rs-icon class=\"indicator\" [class.collapsed]=\"!isExpanded\">expand</rs-icon>\n  }\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card-header{min-height:56px;padding:20px;width:100%;display:flex;align-items:center;font-size:1.3em;border-radius:5px 5px 0 0}:host .rs-card-header.expanded{cursor:pointer;border-radius:5px 5px 0 0}:host .rs-card-header.collapsed{cursor:pointer;border-radius:5px}:host .rs-card-header .indicator{transition-duration:.5s;transition-property:transform}:host .rs-card-header .indicator.collapsed{transform:rotate(-180deg)}\n"], dependencies: [{ kind: "component", type: IconComponent, selector: "rs-icon" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-card-header', standalone: true, imports: [IconComponent], template: "<div tabindex=\"0\" role=\"dialog\" class=\"rs-card-header\" aria-label=\"dialog-content\"\n  [class.expanded]=\"expandable && isExpanded\"\n  [class.collapsed]=\"expandable && !isExpanded\"\n  (click)=\"expand()\"\n  (keydown)=\"expandWithEnter($event)\">\n  <ng-content></ng-content>\n  <span style=\"flex: 1;\"></span>\n  @if (expandable) {\n    <rs-icon class=\"indicator\" [class.collapsed]=\"!isExpanded\">expand</rs-icon>\n  }\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card-header{min-height:56px;padding:20px;width:100%;display:flex;align-items:center;font-size:1.3em;border-radius:5px 5px 0 0}:host .rs-card-header.expanded{cursor:pointer;border-radius:5px 5px 0 0}:host .rs-card-header.collapsed{cursor:pointer;border-radius:5px}:host .rs-card-header .indicator{transition-duration:.5s;transition-property:transform}:host .rs-card-header .indicator.collapsed{transform:rotate(-180deg)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { expanded: [{
                type: Output
            }], expandable: [{
                type: Input
            }], isExpanded: [{
                type: Input
            }], color: [{
                type: Input
            }] } });
/**
 * Card Component
 *
 * @description A papercraft type of comopnent to show depth
 * @example
 * <!-- rs-doc {link: 'Card without header'} -->
 * <rs-card>
 *  <p>Insert standard HTML within the <code>rs-card</code> tag to add content.</p>
 * </rs-card>
 */
class CardComponent {
    constructor() {
        this.header = undefined;
        this.contentExpanded = true;
        this._fullContent = false;
        this._fullContainer = false;
    }
    /**
     * controls the margin on the card outter bounds. shouldn't have to override this css
     */
    get fullContainer() {
        return this._fullContainer;
    }
    set fullContainer(value) {
        this._fullContainer = coerceBooleanProperty(value);
    }
    /**
     * controls the padding on the content. there are some application of this component that need the padding removed
     */
    get fullContent() {
        return this._fullContent;
    }
    set fullContent(value) {
        this._fullContent = coerceBooleanProperty(value);
    }
    ngAfterViewInit() {
        if (this.header) {
            of('').pipe(delay(0)).subscribe(() => this.contentExpanded = this.header.isExpanded);
            this.header.expanded.subscribe(x => this.contentExpanded = x);
        }
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/member-ordering
    static { this.ngAcceptInputType_fullContent = undefined; }
    // eslint-disable-next-line @typescript-eslint/naming-convention, @typescript-eslint/member-ordering
    static { this.ngAcceptInputType_fullContainer = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: CardComponent, isStandalone: true, selector: "rs-card", inputs: { fullContainer: "fullContainer", fullContent: "fullContent" }, queries: [{ propertyName: "header", first: true, predicate: CardHeaderComponent, descendants: true }], ngImport: i0, template: "<!--<ng-content></ng-content>-->\n<ng-content select=\"rs-card-floating-header\"></ng-content>\n<div class=\"rs-card\" [class.full-container]=\"fullContainer\">\n  <ng-content select=\"rs-card-header\"></ng-content>\n  <div [class.rs-card-content-collapsed]=\"!contentExpanded\" [class.full-content]=\"fullContent\" class=\"rs-card-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card{box-shadow:0 6px 16px -1px #00000026;background:#fff;border-radius:5px;margin:30px;position:relative}:host .rs-card.full-container{margin:0}:host .rs-card .rs-card-content{padding:30px 0;transition-delay:.5;transition:height .5s,padding-top .5s,padding-bottom .5s}:host .rs-card .rs-card-content.full-content{padding:0}:host .rs-card .rs-card-content-collapsed{height:0;padding-top:0;padding-bottom:0;overflow:hidden}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-card', standalone: true, template: "<!--<ng-content></ng-content>-->\n<ng-content select=\"rs-card-floating-header\"></ng-content>\n<div class=\"rs-card\" [class.full-container]=\"fullContainer\">\n  <ng-content select=\"rs-card-header\"></ng-content>\n  <div [class.rs-card-content-collapsed]=\"!contentExpanded\" [class.full-content]=\"fullContent\" class=\"rs-card-content\">\n    <ng-content></ng-content>\n  </div>\n</div>\n", styles: [":host *,:host *:before,:host *:after{box-sizing:border-box}:host .rs-card{box-shadow:0 6px 16px -1px #00000026;background:#fff;border-radius:5px;margin:30px;position:relative}:host .rs-card.full-container{margin:0}:host .rs-card .rs-card-content{padding:30px 0;transition-delay:.5;transition:height .5s,padding-top .5s,padding-bottom .5s}:host .rs-card .rs-card-content.full-content{padding:0}:host .rs-card .rs-card-content-collapsed{height:0;padding-top:0;padding-bottom:0;overflow:hidden}\n"] }]
        }], propDecorators: { header: [{
                type: ContentChild,
                args: [CardHeaderComponent, { static: false }]
            }], fullContainer: [{
                type: Input
            }], fullContent: [{
                type: Input
            }] } });

class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: CardModule, imports: [CommonModule,
            IconModule,
            CardComponent, CardHeaderComponent, CardFloatingHeaderComponent], exports: [CardComponent, CardHeaderComponent, CardFloatingHeaderComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardModule, imports: [CommonModule,
            IconModule,
            CardHeaderComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        IconModule,
                        CardComponent, CardHeaderComponent, CardFloatingHeaderComponent
                    ],
                    exports: [CardComponent, CardHeaderComponent, CardFloatingHeaderComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { CardComponent, CardFloatingHeaderComponent, CardHeaderComponent, CardModule };
